// Libs
import React from 'react';
import { useSelector } from 'react-redux';

// Selectors
import { selectResizableElementsGroup } from './store/resizingSelector';

// Utils
import { getMeasurementsMap } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';

// Actions
import { measurementsSet } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsActions';
import { useMeasurementsDispatch } from '../../components/measurementsStore/MeasurementsProvider';

// Components
import Icon from '../../components/icons/Icon';
import useResizeHandlePointerHandlers from './useResizeHandlePointerHandlers';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { ElementMeasurement } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { ResizeAspectLock, SetElementSizeAndPosition, SetTempElementSize } from './resizeTypes';

// Styles
import './ElementResizeHandle.scss';
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';

type Props = {
    element: ImMNElement;
    elementId: string;
    gridSize: number;
    showHandle: boolean;
    onPointerDown: (event: React.PointerEvent) => void;
    onTouchStart: (event: React.TouchEvent) => void;
    onDoubleClick: (event: React.MouseEvent) => void;
    documentMode: boolean;
    getCurrentMeasurements: (elementId: string) => Map<string, number>;
    getContextZoomScale: () => number;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    setTempElementSize: SetTempElementSize;
    tempSize: RectSize | null;
    isResizing: boolean;
    resizeAspectLock: ResizeAspectLock;
    minWidth?: number;
    minHeight?: number;
    skipSizeUpdate?: boolean;
    repositionOnly?: boolean;
    allowAspectChangeForCurrentResize: boolean;
};

const ElementResizeHandle = (props: Props) => {
    const { showHandle, documentMode, element } = props;

    const measurementDispatch = useMeasurementsDispatch();
    const getCurrentMeasurements = (elementId: string): Map<string, number> => {
        // @ts-ignore - thunk doesn't take in an action
        return measurementDispatch((_dispatch: Function, getState: Function) => {
            const state = getState();
            const measurementsMap = getMeasurementsMap(state);
            return measurementsMap.get(elementId);
        });
    };
    const dispatchMeasurementsSet = ({ id, measurements }: { id: string; measurements: ElementMeasurement }) => {
        measurementDispatch(measurementsSet({ id, measurements }));
    };

    const resizableElementsGroup = useSelector(selectResizableElementsGroup);
    const isResizingGroup = resizableElementsGroup.size && resizableElementsGroup.includes(element);
    const resizingElements = isResizingGroup ? resizableElementsGroup : [element];

    const { onPointerDown, onTouchStart, onDoubleClick } = useResizeHandlePointerHandlers(
        props,
        resizingElements,
        getCurrentMeasurements,
        dispatchMeasurementsSet,
    );

    if (!showHandle || documentMode) return null;

    return (
        <div
            className="ElementResizeHandle"
            onPointerDown={onPointerDown}
            onTouchStart={onTouchStart}
            onDoubleClick={onDoubleClick}
        >
            <Icon name="element-resize-handle" />
        </div>
    );
};

export default ElementResizeHandle;
